import React from 'react';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import * as api from 'src/utils/api';
import useGetRequest from 'src/hooks/useGetRequest';
import { Perspective, Widget, WidgetType } from 'src/types';

import { WIDGET_DETAILS_BY_WIDGET_TYPE } from 'src/views/constants/widgetConstants';
import { FIELD_ID, PERSPECTIVE_ID, WIDGET_TYPE } from 'src/views/constants/perspectiveConstants';

interface Props {
  workflowId: string,
  perspectiveId: string,
  defaultLoadingState?: boolean,
  onSelectWidget?: (widgetId: string) => void,
}

const usePerspective = ({
  workflowId,
  perspectiveId,
  defaultLoadingState,
  onSelectWidget,
}: Props) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [perspective, setPerspective] = React.useState<Perspective>();
  const [widgetsByWidgetId, setWidgetsByWidgetId] = React.useState<Record<string, Widget>>({});
  const [selectedWidgetId, setSelectedWidgetId] = React.useState<string>();
  const { httpRequest, isLoading } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/perspectives/${perspectiveId}/`,
    initialResult: { widgets: [] },
    defaultLoadingState,
    onSuccess: (_perspective: Perspective) => {
      const widgetsById: Record<string, Widget> = {};
      // filter out widgets with this perspectiveId
      Object.values(widgetsByWidgetId).forEach((_widget) => {
        if (perspectiveId && _widget.perspectiveId.toString() !== perspectiveId.toString()) { // todo - uuid
          widgetsById[_widget.id] = _widget;
        }
      });

      _perspective.widgets.forEach((_widget: any) => {
        widgetsById[_widget.id] = _widget;
      });
      setWidgetsByWidgetId(widgetsById);
      setPerspective(_perspective);
    }
  });
  const widgets = Object.values(widgetsByWidgetId).map((_widget) => ({ ..._widget, id: _widget.id.toString() })); // hack isnt needed when switch to uuid
  const selectWidget = (widgetId: any) => {
    setSelectedWidgetId(widgetId);
    if (onSelectWidget) onSelectWidget(widgetId);
  };
  const addWidget = (_widget: any) => {
    api.createWidget(workflowId, perspectiveId, _widget).then((res) => {
      setWidgetsByWidgetId((_widgets) => ({
        ..._widgets,
        [res.id]: res,
      }));
      selectWidget(res.id);
    }).catch(() => {
      enqueueSnackbar('Failed to add widget', {
        variant: 'error'
      });
    });
  };
  const removeWidget = (widgetId: any) => setWidgetsByWidgetId((_widgets) => {
    const result = { ..._widgets };
    delete result[widgetId];
    return result;
  });
  const onDragStop = (_: any, __: any, { i: id, x, y }: any) => {
    const updates = { x, y };
    setIsDragging(false);
    api.saveWidget(workflowId, perspectiveId, id, updates).then((res) => {
      setWidgetsByWidgetId((_widgetsByWidgetId) => ({
        ..._widgetsByWidgetId,
        [id]: {
          ..._widgetsByWidgetId[id],
          x,
          y,
        },
      }));
    });
  };
  const onResizeStop = (_: any, __: any, { i: id, w, h }: any) => {
    api.saveWidget(workflowId, perspectiveId, id, { width: w, height: h }).then((res) => {
      setWidgetsByWidgetId((_widgets) => ({
        ..._widgets,
        [res.id]: res,
      }));
    });
  };
  const updateWidget = (id: any, updates: any) => api.saveWidget(workflowId, perspectiveId, id, updates).then((res) => {
    setWidgetsByWidgetId((_widgets) => ({
      ..._widgets,
      [res.id]: res,
    }));
  });
  const onDrop = (layout: any, layoutItem: any, event: any) => {
    if (!layoutItem) return; // todo - idk why this happens
    const widgetType: WidgetType = event.dataTransfer.getData(WIDGET_TYPE);
    const details = WIDGET_DETAILS_BY_WIDGET_TYPE[widgetType];
    addWidget({
      type: widgetType,
      x: layoutItem.x,
      y: layoutItem.y,
      width: details.droppingItem.w,
      height: details.droppingItem.h,
      fieldId: event.dataTransfer.getData(FIELD_ID) || null,
      data: {
        ...details.defaultWidget,
      },
    });
    setIsDragging(false);
  };
  const onDragStart = () => {
    setIsDragging(true);
  };
  const saveWidget = (widgetId: any, widgetPayload: any) => updateWidget(widgetId, widgetPayload);
  const deleteWidget = (widgetId: any) => api.deleteWidget(workflowId, perspectiveId, widgetId).then(() => {
    removeWidget(widgetId);
    httpRequest();
  });

  const selectedWidget = selectedWidgetId ? widgetsByWidgetId[selectedWidgetId] : undefined;
  return {
    onDragStart,
    onDragStop,
    onDrop,
    onResizeStop,
    isDragging,
    setIsDragging,
    selectWidget,
    saveWidget,
    deleteWidget,
    getPerspective: httpRequest,
    widgets,
    perspective,
    selectedWidget,
    isLoading,
  };
};
export default usePerspective;
